/**
 * Spec 0050 (U3) — per-product resource-limit **plans** (tiers) + the per-plan
 * **limit-values editor** (saas-auth SASATH-0062). SUPER_ADMIN-only, mounted on
 * the product detail screen (the caller in ProductDetail gates rendering on
 * `operator?.isSuperAdmin`; this component additionally degrades to a contained
 * "SUPER_ADMIN required" state if the API returns 403). Self-fetches BOTH the
 * plan list AND the product's definitions on mount with isolated
 * loading/empty/error states — it is NOT folded into the shared
 * productDetailLoader, so an ordinary SAAS_ADMIN viewer's product page is never
 * broken by a 403 here.
 *
 * A plan is a named tier (`name`, optional `description`, `active`). The limits
 * editor renders the product's definitions with a TYPED input per lever:
 * INT → number field, BOOL → toggle, STRING → text. Inputs are PRE-FILLED from
 * the plan's current values, which arrive EMBEDDED on the plan
 * (`plan.limits` — SASATH-0064; no separate plan-values read or definitions
 * join). Save upserts the full value set via `PUT …/plans/{planId}/limits`
 * (`setPlanLimits`), with client-side value-type validation mirroring the
 * backend's (an INT value must parse as a whole number).
 *
 * Encoding (gap #2 → strings): lever values are strings interpreted per the
 * definition's `valueType`. INT/STRING inputs left blank mean "this plan sets no
 * value for the lever" → OMITTED from the upsert (the tenant falls back to the
 * definition default). A BOOL toggle is binary, so it always contributes a
 * `'true'`/`'false'` value.
 *
 * DELETE reflects the backend's in-use convention WITHOUT assuming one: on a 2xx
 * the row is dropped; on a rejection the backend's own message/code is surfaced
 * via the snackbar and the row stays (an assigned-plan block surfaces as its
 * rejection).
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneIcon from '@mui/icons-material/Tune';
import { useEffect, useState } from 'react';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type { LimitDefinition, LimitValueType, Plan, PlanLimitValue } from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';

function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

/**
 * Validate that a raw limit value PARSES for the value type, mirroring the
 * backend's write-time check (U2's `defaultValidationKey`). Returns the i18n
 * suffix of the validation error, or null when valid. A blank value is valid
 * (it means "this plan sets no value" → omitted from the upsert). BOOL is driven
 * by a toggle (always `true`/`false`) and STRING accepts any text, so only INT
 * needs a parse guard here.
 */
function limitValidationKey(valueType: LimitValueType, raw: string): string | null {
  const v = raw.trim();
  if (v === '') return null;
  if (valueType === 'INT') return /^-?\d+$/.test(v) ? null : 'badInt';
  return null;
}

export function ProductPlans({ productId }: { productId: number }) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [definitions, setDefinitions] = useState<LimitDefinition[]>([]);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPlan, setEditPlan] = useState<Plan | null>(null);
  const [limitsPlan, setLimitsPlan] = useState<Plan | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Plan | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setLoadError(null);
    try {
      // Both reads are SUPER_ADMIN-gated; the editor needs the definitions to
      // render its typed inputs, so fetch them alongside the plans.
      const [planList, defList] = await Promise.all([
        apiAdmin.resourceLimits.plans.list(productId),
        apiAdmin.resourceLimits.definitions.list(productId),
      ]);
      setPlans(planList);
      setDefinitions(defList);
      setForbidden(false);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setPlans([]);
        return;
      }
      if (err instanceof ApiError) setLoadError(err);
      setPlans([]);
    }
  }

  useEffect(() => {
    // `refresh()` only setStates after an `await`; wrap it in an inline async
    // IIFE so the effect body holds no synchronous setState (behavior identical).
    void (async () => {
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  function openAdd() {
    setEditPlan(null);
    setDialogOpen(true);
  }

  function openEdit(plan: Plan) {
    setEditPlan(plan);
    setDialogOpen(true);
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await apiAdmin.resourceLimits.plans.remove(productId, confirmDelete.id);
      snack.success(t('plans.deleted', 'Plan deleted.'));
      setConfirmDelete(null);
      await refresh();
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setConfirmDelete(null);
        return;
      }
      // Reflect the backend's in-use convention WITHOUT assuming one: surface the
      // backend's own message (e.g. an assigned-plan block) and keep the row.
      if (err instanceof ApiError) {
        snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
      }
      setConfirmDelete(null);
    } finally {
      setBusy(false);
    }
  }

  if (forbidden) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h3" gutterBottom>
          {t('plans.title', 'Resource-limit plans')}
        </Typography>
        <Alert severity="warning">
          {t('plans.superAdminRequired', 'Super admin required to manage resource-limit plans.')}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="h3">{t('plans.title', 'Resource-limit plans')}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={openAdd}>
          {t('plans.add', 'Add plan')}
        </Button>
      </Stack>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2,
        }}
      >
        {t(
          'plans.explanation',
          'Each plan is a named tier with a value per limit lever. Assign a plan to a tenant to grant its values; a lever a plan leaves unset falls back to the definition default. Edit a plan’s values with the tune control.',
        )}
      </Typography>
      <ErrorBanner error={loadError} />
      {plans === null ? (
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('common.loading', 'Loading')}
        </Typography>
      ) : plans.length === 0 ? (
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('plans.empty', 'No plans for this product yet.')}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('plans.columns.name', 'Name')}</TableCell>
                <TableCell>{t('plans.columns.description', 'Description')}</TableCell>
                <TableCell>{t('plans.columns.active', 'Active')}</TableCell>
                <TableCell>{t('plans.columns.limits', 'Limits')}</TableCell>
                <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell>{plan.name}</TableCell>
                  <TableCell>
                    {plan.description ? (
                      plan.description
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {t('plans.noDescription', '—')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        plan.active
                          ? t('plans.statuses.active', 'Active')
                          : t('plans.statuses.inactive', 'Inactive')
                      }
                      color={plan.active ? 'success' : 'default'}
                      size="small"
                      variant={plan.active ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <PlanLimitsSummary plan={plan} definitions={definitions} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label={t('plans.editLimits', 'Edit limit values')}
                      title={t('plans.editLimits', 'Edit limit values')}
                      onClick={() => setLimitsPlan(plan)}
                    >
                      <TuneIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label={t('common.edit', 'Edit')}
                      onClick={() => openEdit(plan)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label={t('common.delete', 'Delete')}
                      onClick={() => setConfirmDelete(plan)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <PlanDialog
        open={dialogOpen}
        productId={productId}
        plan={editPlan}
        onClose={() => setDialogOpen(false)}
        onForbidden={() => {
          setDialogOpen(false);
          setForbidden(true);
        }}
        onSaved={() => {
          setDialogOpen(false);
          void refresh();
        }}
      />
      <PlanLimitsDialog
        open={limitsPlan !== null}
        productId={productId}
        plan={limitsPlan}
        definitions={definitions}
        onClose={() => setLimitsPlan(null)}
        onForbidden={() => {
          setLimitsPlan(null);
          setForbidden(true);
        }}
        onSaved={() => {
          setLimitsPlan(null);
          void refresh();
        }}
      />
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={t('plans.deleteTitle', 'Delete plan?')}
        body={t(
          'plans.deleteBody',
          'Delete {{name}}? A tenant assigned this plan must be reassigned first per the backend. This cannot be undone.',
          { name: confirmDelete?.name ?? '' },
        )}
        severity="error"
        confirmLabel={t('common.delete', 'Delete')}
        busy={busy}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Paper>
  );
}

/**
 * The read-only comparison cell for one plan: every product definition listed in
 * the SAME order on every row as `label: value`, so plans line up side by side.
 * `value` is the plan's SET value (`plan.limits` keyed by `definitionId`) when
 * present; otherwise the definition's `defaultValue` marked `(default)`; or `—`
 * when the plan sets no value AND the definition has no default. The lever set is
 * derived from `definitions` + `plan.limits` (no effect/state) — it re-derives on
 * each render from the section's already-fetched definitions.
 */
function PlanLimitsSummary({
  plan,
  definitions,
}: {
  plan: Plan;
  definitions: LimitDefinition[];
}) {
  const { t } = useCopy();
  if (definitions.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('plans.limitNone', '—')}
      </Typography>
    );
  }
  const setLimits = plan.limits;
  return (
    <Stack spacing={0.25}>
      {definitions.map((def) => {
        const set = setLimits.find((lv) => lv.definitionId === def.id);
        const display = set
          ? set.value
          : def.defaultValue !== null
            ? t('plans.limitDefault', '{{value}} (default)', { value: def.defaultValue })
            : t('plans.limitNone', '—');
        return (
          <Typography key={def.id} variant="body2">
            {`${def.label}: ${display}`}
          </Typography>
        );
      })}
    </Stack>
  );
}

interface PlanDialogProps {
  open: boolean;
  productId: number;
  plan: Plan | null;
  onClose: () => void;
  onForbidden: () => void;
  onSaved: () => void;
}

function PlanDialog({ open, productId, plan, onClose, onForbidden, onSaved }: PlanDialogProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const isEdit = plan !== null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevPlan, setPrevPlan] = useState(plan);
  const [pending, setPending] = useState(false);

  // Seed the form from the opened plan via a render-phase state adjustment
  // (React's "adjust state while rendering" pattern) instead of a synchronous
  // setState in an effect — re-applies on the same open/plan changes.
  if (open !== prevOpen || plan !== prevPlan) {
    setPrevOpen(open);
    setPrevPlan(plan);
    if (open) {
      setName(plan?.name ?? '');
      setDescription(plan?.description ?? '');
      setActive(plan?.active ?? true);
      setError(null);
    }
  }

  async function onSubmit() {
    setError(null);
    setPending(true);
    const trimmedDescription = description.trim();
    // Blank description → null ("no description"); otherwise the typed text.
    const wireDescription = trimmedDescription === '' ? null : trimmedDescription;
    try {
      if (isEdit && plan) {
        await apiAdmin.resourceLimits.plans.update(productId, plan.id, {
          name: name.trim(),
          description: wireDescription,
          active,
        });
        snack.success(t('plans.updated', 'Plan updated.'));
      } else {
        await apiAdmin.resourceLimits.plans.create(productId, {
          name: name.trim(),
          description: wireDescription,
          active,
        });
        snack.success(t('plans.created', 'Plan created.'));
      }
      onSaved();
    } catch (err) {
      if (isForbidden(err)) {
        onForbidden();
        return;
      }
      if (err instanceof ApiError) setError(err);
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? t('plans.edit', 'Edit plan') : t('plans.add', 'Add plan')}
      </DialogTitle>
      <DialogContent>
        <ErrorBanner error={error} />
        <Box
          component="form"
          id="plan-form"
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmit();
          }}
          noValidate
        >
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              required
              label={t('plans.fields.name', 'Name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t('plans.fields.description', 'Description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              helperText={t('plans.fields.descriptionHelper', 'Optional. Leave blank for none.')}
            />
            <FormControlLabel
              control={
                <Switch checked={active} onChange={(e) => setActive(e.target.checked)} />
              }
              label={t('plans.fields.active', 'Active')}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button form="plan-form" type="submit" variant="contained" disabled={pending}>
          {t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface PlanLimitsDialogProps {
  open: boolean;
  productId: number;
  plan: Plan | null;
  definitions: LimitDefinition[];
  onClose: () => void;
  onForbidden: () => void;
  onSaved: () => void;
}

function PlanLimitsDialog({
  open,
  productId,
  plan,
  definitions,
  onClose,
  onForbidden,
  onSaved,
}: PlanLimitsDialogProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  // The per-definition raw input value, keyed by definitionId. BOOL is stored as
  // the strings 'true'/'false' (toggle state); INT/STRING hold the typed text.
  const [values, setValues] = useState<Record<number, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({});
  const [error, setError] = useState<ApiError | null>(null);
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevPlan, setPrevPlan] = useState(plan);
  const [pending, setPending] = useState(false);

  // Seed the editor from the plan's EMBEDDED current values (Option A) via a
  // render-phase state adjustment — re-applies on the same open/plan changes. A
  // definition absent from `plan.limits` starts blank (INT/STRING) or off
  // (BOOL → undefined renders as false).
  if (open !== prevOpen || plan !== prevPlan) {
    setPrevOpen(open);
    setPrevPlan(plan);
    if (open) {
      const seed: Record<number, string> = {};
      for (const lv of plan?.limits ?? []) seed[lv.definitionId] = lv.value;
      setValues(seed);
      setFieldErrors({});
      setError(null);
    }
  }

  function setValue(definitionId: number, raw: string) {
    setValues((prev) => ({ ...prev, [definitionId]: raw }));
    setFieldErrors((prev) => {
      if (!(definitionId in prev)) return prev;
      const next = { ...prev };
      delete next[definitionId];
      return next;
    });
  }

  async function onSubmit() {
    if (!plan) return;
    setError(null);
    // Validate every lever's value PARSES for its type before any network call.
    const errs: Record<number, string> = {};
    for (const def of definitions) {
      const invalid = limitValidationKey(def.valueType, values[def.id] ?? '');
      if (invalid) errs[def.id] = invalid;
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    // Build the full upsert set: a BOOL toggle always contributes 'true'/'false';
    // an INT/STRING left blank means "no value for this lever" → omitted (the
    // tenant falls back to the definition default).
    const payload: PlanLimitValue[] = definitions.flatMap((def) => {
      const raw = (values[def.id] ?? '').trim();
      if (def.valueType === 'BOOL') {
        return [{ definitionId: def.id, value: raw === 'true' ? 'true' : 'false' }];
      }
      if (raw === '') return [];
      return [{ definitionId: def.id, value: raw }];
    });
    setPending(true);
    try {
      await apiAdmin.resourceLimits.setPlanLimits(productId, plan.id, payload);
      snack.success(t('plans.limitsSaved', 'Plan limits saved.'));
      onSaved();
    } catch (err) {
      if (isForbidden(err)) {
        onForbidden();
        return;
      }
      if (err instanceof ApiError) setError(err);
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('plans.limitsTitle', 'Edit limit values — {{name}}', { name: plan?.name ?? '' })}
      </DialogTitle>
      <DialogContent>
        <ErrorBanner error={error} />
        {definitions.length === 0 ? (
          <Typography
            sx={{
              color: 'text.secondary',
              mt: 1,
            }}
          >
            {t(
              'plans.noDefinitions',
              'This product has no limit definitions yet. Add definitions above before setting plan values.',
            )}
          </Typography>
        ) : (
          <Box
            component="form"
            id="plan-limits-form"
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmit();
            }}
            noValidate
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 1,
                mb: 2,
              }}
            >
              {t(
                'plans.limitsExplanation',
                'Set this plan’s value for each lever. Leave a number or text lever blank to use the definition default.',
              )}
            </Typography>
            <Stack spacing={2}>
              {definitions.map((def) => (
                <LimitField
                  key={def.id}
                  definition={def}
                  value={values[def.id] ?? ''}
                  errorKey={fieldErrors[def.id] ?? null}
                  onChange={(raw) => setValue(def.id, raw)}
                />
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          form="plan-limits-form"
          type="submit"
          variant="contained"
          disabled={pending || definitions.length === 0}
        >
          {t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface LimitFieldProps {
  definition: LimitDefinition;
  value: string;
  errorKey: string | null;
  onChange: (raw: string) => void;
}

/**
 * One typed input for a definition: INT → number field, BOOL → toggle,
 * STRING → text field. The label is the lever's `label`; the `key` is shown as
 * helper context.
 */
function LimitField({ definition, value, errorKey, onChange }: LimitFieldProps) {
  const { t } = useCopy();
  const helper =
    errorKey !== null
      ? t(`plans.validation.${errorKey}`, 'Invalid value')
      : definition.key;

  if (definition.valueType === 'BOOL') {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={value === 'true'}
            onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          />
        }
        label={`${definition.label} (${definition.key})`}
      />
    );
  }

  return (
    <TextField
      fullWidth
      type={definition.valueType === 'INT' ? 'number' : 'text'}
      label={definition.label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={errorKey !== null}
      helperText={helper}
    />
  );
}
